import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AppLogo from "@/assets/images/favicon.png";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import {
	useCreateCheckoutSessionMutation,
	useGetStripeProductsQuery,
} from "../../redux/subscription/subscription.api";
import { SubscriptionCard } from "./components/SubscriptionCard";
import { StripeProduct } from "./schema/stripe-product";
import { getSubscriptionFeatures } from "./utils/features";

type PaymentInterval = "month" | "quarter" | "year" | "semi-annual";

type StripeProductPrice = StripeProduct["prices"][number];

const isInstallmentPrice = (price: { isSemiAnnual?: boolean; isQuarterly?: boolean }) =>
	price.isSemiAnnual === true || price.isQuarterly === true;

export const Subscription: FunctionComponent = () => {
	const { t } = useTranslation();
	const { data: stripeProducts } = useGetStripeProductsQuery();
	const { user } = useAppSelector(authSelector);
	const allowInstallments = user?.company?.allowInstallments === true;

	const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
	const [paymentInterval, setPaymentInterval] = useState<PaymentInterval>("month");
	const [checkoutError, setCheckoutError] = useState<string | null>(null);

	const [createCheckoutSession, { isLoading: isCreatingCheckoutSession }] =
		useCreateCheckoutSessionMutation();

	const getProductPrice = (product: StripeProduct): StripeProductPrice | undefined => {
		if (paymentInterval === "semi-annual") {
			return allowInstallments
				? product.prices.find((price) => price.isSemiAnnual === true)
				: undefined;
		}

		if (paymentInterval === "quarter") {
			return allowInstallments
				? product.prices.find((price) => price.isQuarterly === true)
				: undefined;
		}

		return product.prices.find(
			(price) => price.interval === paymentInterval && !isInstallmentPrice(price)
		);
	};

	const intervalLabel = useMemo(() => {
		if (paymentInterval === "semi-annual") {
			return t("usage.subscription.intervalLabels.halfYear");
		}

		if (paymentInterval === "quarter") {
			return t("usage.subscription.intervalLabels.quarter");
		}

		if (paymentInterval === "year") {
			return t("usage.subscription.intervalLabels.year");
		}

		return t("usage.subscription.intervalLabels.month");
	}, [paymentInterval, t]);

	const availableProducts = useMemo(() => {
		return (
			stripeProducts
				?.map((product) => ({ product, price: getProductPrice(product) }))
				?.filter((entry): entry is { product: StripeProduct; price: StripeProductPrice } =>
					Boolean(entry.price)
				) ?? []
		);
	}, [stripeProducts, paymentInterval, allowInstallments]);

	const selectedEntry = useMemo(
		() => availableProducts.find((entry) => entry.product.id === selectedProductId),
		[availableProducts, selectedProductId]
	);

	const handleCreateCheckoutSession = async () => {
		if (!selectedEntry) {
			return;
		}

		setCheckoutError(null);

		try {
			const { sessionUrl } = await createCheckoutSession({
				priceId: selectedEntry.price.id,
				productId: selectedEntry.product.id,
			}).unwrap();

			window.location.href = sessionUrl;
		} catch (err: unknown) {
			const error = err as { status?: number; data?: { message?: string } };

			if (error?.status === 403) {
				setCheckoutError(
					error?.data?.message || t("usage.subscription.select.installmentsNotAllowed")
				);
			}
		}
	};

	const intervalButtons: { interval: PaymentInterval; label: string; installment: boolean }[] = [
		{ interval: "month", label: t("usage.subscription.intervals.monthly"), installment: false },
		{ interval: "quarter", label: t("usage.subscription.intervals.quarterly"), installment: true },
		{ interval: "year", label: t("usage.subscription.intervals.annually"), installment: false },
		{
			interval: "semi-annual",
			label: t("usage.subscription.intervals.semiAnnual"),
			installment: true,
		},
	];

	return (
		<div className="w-full flex flex-col items-center">
			<div className="w-full z-10 flex flex-col justify-center items-center mb-8">
				<img alt="logo" className="w-20 h-20" src={AppLogo} />
				<div className="mb-2 text-md">{t("usage.subscription.card.heading")}</div>
				<div className="flex flex-row gap-2 items-center justify-center">
					{intervalButtons
						.filter(({ installment }) => allowInstallments || !installment)
						.map(({ interval, label }) => (
							<button
								key={interval}
								className={classNames(
									"px-3 py-1.5 rounded-lg text-sm transition-colors",
									paymentInterval === interval
										? "bg-blue-action text-white font-bold"
										: "text-text-meta font-normal hover:bg-background-light"
								)}
								type="button"
								onClick={() => setPaymentInterval(interval)}
							>
								{label}
							</button>
						))}
				</div>
			</div>
			{stripeProducts === undefined ? (
				<div>Loading...</div>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 m-4 justify-center">
					{availableProducts.map(({ product, price }) => {
						const displayCredits =
							paymentInterval === "month" ? product.credits : product.credits * 12;

						return (
							<SubscriptionCard
								key={product.id}
								creditsLabel={t("usage.subscription.select.creditsLabel", {
									credits: displayCredits,
									interval: intervalLabel,
									costPerCredit: price.costsPerCredit,
								})}
								description={product.description || ""}
								features={getSubscriptionFeatures(product.credits, t)}
								includedTitle={t("usage.subscription.card.whatsIncluded")}
								monthlyPrice={price.monthlyPrice}
								name={product.name}
								perMonth={t("usage.subscription.select.perMonth")}
								selectTitle={
									product.id === selectedProductId
										? t("usage.subscription.card.selected")
										: t("usage.subscription.card.select")
								}
								variant="default"
								onSelect={() => setSelectedProductId(product.id)}
							/>
						);
					})}
				</div>
			)}
			<div className="text-sm text-gray-700 w-full max-w-[1082px]">
				{paymentInterval === "year" && t("usage.subscription.footnote.yearUpfront")}
				{paymentInterval === "quarter" && t("usage.subscription.footnote.quarterCommitment")}
				{paymentInterval === "semi-annual" && t("usage.subscription.footnote.semiAnnualCommitment")}
			</div>
			{checkoutError && (
				<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 my-4 w-full max-w-[400px] text-center">
					{checkoutError}
				</div>
			)}
			<div className="w-[250px] flex justify-center mt-4">
				<Button
					color={ButtonColor.ACTION}
					disabled={!selectedEntry}
					isLoading={isCreatingCheckoutSession}
					size={ButtonSize.ML}
					title={t("usage.subscription.card.continueToPayment")}
					onClick={handleCreateCheckoutSession}
				/>
			</div>
		</div>
	);
};
